// src/components/trip/MembersTab.tsx
import React, { useState } from 'react';
import { View, Heading, Button, Flex, Alert, CheckboxField, Text, Card } from '@aws-amplify/ui-react';
import { TripRole } from '../../API';
import { Link, Users, Trash, X, AlertCircle } from 'lucide-react';
import MemberList from './MemberList';
import InviteLinkModal from '../InviteLinkModal';
import { removeTripMember, MemberRemovalResult } from '../../services/tripMemberActions';
import { formatFileSize } from '../../utils/formatters';

interface MembersTabProps {
  members: any[];
  tripId: string;
  tripName: string;
  userRole: string | null;
  userId?: string;
  onMemberRemoved?: (memberId: string, deletedPhotoIds?: string[]) => void;
}

// Custom modal component to replace @aws-amplify/ui-react Modal
const ConfirmationDialog: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
}> = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  confirmText = "Confirm", 
  cancelText = "Cancel",
  onConfirm 
}) => {
  if (!isOpen) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem'
      }}
    >
      <Card 
        style={{
          maxWidth: '500px',
          width: '100%',
          backgroundColor: '#1A1A1A',
          boxShadow: '0px 8px 30px rgba(0, 0, 0, 0.5)',
          borderRadius: '8px'
        }}
      >
        <Flex direction="column" padding="1.5rem" gap="1.5rem">
          <Flex alignItems="center" gap="1rem">
            <AlertCircle size={24} color="#FF4B4B" />
            <Heading level={4}>{title}</Heading>
          </Flex>
          
          {children}
          
          <Flex direction="row" justifyContent="flex-end" gap="1rem" marginTop="1rem">
            <Button
              onClick={onClose}
              variation="link"
            >
              {cancelText}
            </Button>
            
            <Button
              onClick={onConfirm}
              variation="destructive"
            >
              {confirmText}
            </Button>
          </Flex>
        </Flex>
      </Card>
    </div>
  );
};

const MembersTab: React.FC<MembersTabProps> = ({
  members,
  tripId,
  tripName,
  userRole,
  userId,
  onMemberRemoved
}) => {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectionMode, setSelectionMode] = useState<boolean>(false);
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [deletePhotos, setDeletePhotos] = useState<boolean>(false);
  const [removalResults, setRemovalResults] = useState<MemberRemovalResult[]>([]);
  
  // Check if user can edit (PARTICIPANT or CREATOR)
  const canEdit = userRole === TripRole.PARTICIPANT || userRole === 'CREATOR';
  
  // Check if user is the creator (only creators can remove members)
  const isCreator = userRole === 'CREATOR';

  // Toggle selection mode
  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedMembers(new Set());
  };

  // Toggle member selection
  const toggleMemberSelection = (memberId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click from navigating
    
    setSelectedMembers(prevSelected => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(memberId)) {
        newSelected.delete(memberId);
      } else {
        newSelected.add(memberId);
      }
      return newSelected;
    });
  };

  // Open confirmation dialog for member deletion
  const openDeleteConfirmation = () => {
    const memberCount = selectedMembers.size;
    if (memberCount === 0) return;
    
    setDeletePhotos(false);
    setShowConfirmModal(true);
  };

  // Close confirmation dialog
  const closeConfirmModal = () => {
    setShowConfirmModal(false);
  };

  // Delete selected members
  const deleteSelectedMembers = async () => {
    setShowConfirmModal(false);
    setIsDeleting(true);
    setDeleteError(null);
    setDeleteSuccess(false);
    setRemovalResults([]);
    
    try {
      // Process each member removal one at a time
      const deletedMemberIds: string[] = [];
      const deletedPhotoIds: string[] = [];
      const results: MemberRemovalResult[] = [];
      
      // Convert Set to Array before iterating
      const memberIdsArray = Array.from(selectedMembers);
      
      for (const memberId of memberIdsArray) {
        try {
          const result = await removeTripMember(memberId, tripId, deletePhotos);
          
          deletedMemberIds.push(memberId);
          results.push(result);
          
          // Notify parent component
          if (onMemberRemoved) {
            onMemberRemoved(memberId, deletedPhotoIds);
          }
        } catch (err) {
          console.error(`Failed to remove member ${memberId}:`, err);
        }
      }
      
      // Store results for display
      setRemovalResults(results);
      
      if (deletedMemberIds.length === memberIdsArray.length) {
        setDeleteSuccess(true);
        
        // Clear selection after successful deletion
        setSelectedMembers(new Set());
        setSelectionMode(false);
      } else if (deletedMemberIds.length > 0) {
        setDeleteSuccess(true);
        setDeleteError(`${memberIdsArray.length - deletedMemberIds.length} member(s) could not be removed.`);
        
        // Update selection to only include the failed ones
        const failedMembers = new Set(
          memberIdsArray.filter(id => !deletedMemberIds.includes(id))
        );
        setSelectedMembers(failedMembers);
      } else {
        setDeleteError('Failed to remove members. Please try again.');
      }
    } catch (error) {
      console.error('Error removing members:', error);
      setDeleteError('An error occurred while removing members.');
    } finally {
      setIsDeleting(false);
      
      // Auto-clear success message after 3 seconds
      if (deleteSuccess) {
        setTimeout(() => setDeleteSuccess(false), 3000);
      }
    }
  };

  // Calculate total storage freed from deleted photos
  const getTotalStorageFreed = () => {
    return removalResults.reduce((total, result) => {
      return total + (result.storageFreed || 0);
    }, 0);
  };

  return (
    <View>
      {/* Add header with invite button */}
      <Flex direction="row" justifyContent="space-between" alignItems="center" marginBottom="1.5rem">
        <Heading level={3}>Album Members</Heading>

        <Flex gap="1rem">
          {/* Selection mode button - only visible to creators */}
          {isCreator && members.length > 0 && (
            <Button 
              variation={selectionMode ? "primary" : "destructive"}
              onClick={toggleSelectionMode}
            >
              {selectionMode ? "Cancel Selection" : "Manage Members"}
            </Button>
          )}
          
          {/* Delete button - only visible in selection mode */}
          {selectionMode && (
            <Button 
              variation="destructive" 
              onClick={openDeleteConfirmation}
              isLoading={isDeleting}
              isDisabled={selectedMembers.size === 0}
            >
              <Flex alignItems="center" gap="0.5rem">
                <Trash size={16} />
                <span>Remove {selectedMembers.size > 0 ? `(${selectedMembers.size})` : 'Selected'}</span>
              </Flex>
            </Button>
          )}

          {canEdit && !selectionMode && (
            <Button
              onClick={() => setShowInviteModal(true)}
              variation="primary"
            >
              <Flex alignItems="center" gap="0.5rem">
                <Link size={16} />
                <span>Create Invite Link</span>
              </Flex>
            </Button>
          )}
        </Flex>
      </Flex>

      {/* Status messages */}
      {deleteError && (
        <Alert 
          variation="error" 
          isDismissible={true}
          onDismiss={() => setDeleteError(null)}
          marginBottom="1rem"
        >
          {deleteError}
        </Alert>
      )}
      
      {deleteSuccess && (
        <Alert 
          variation="success" 
          isDismissible={true}
          onDismiss={() => setDeleteSuccess(false)}
          marginBottom="1rem"
        >
          <Flex direction="column" gap="0.5rem">
            <Text color="#0A4520" fontWeight="medium">
                Successfully removed {removalResults.length} member{removalResults.length !== 1 ? 's' : ''}.
            </Text>
            
            {deletePhotos && removalResults.some(r => r.photosDeleted) && (
              <Text>
                Total storage freed: {formatFileSize(getTotalStorageFreed())}
              </Text>
            )}
          </Flex>
        </Alert>
      )}

      {/* Member list */}
      <MemberList 
        members={members} 
        currentUserId={userId}
        isSelectionMode={selectionMode}
        selectedMembers={selectedMembers}
        onMemberSelect={toggleMemberSelection}
        isCreator={isCreator}
      />

      {/* Custom Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmModal}
        onClose={closeConfirmModal}
        title={`Remove ${selectedMembers.size} member${selectedMembers.size !== 1 ? 's' : ''}?`}
        confirmText="Confirm Removal"
        onConfirm={deleteSelectedMembers}
      >
        <Text>
          They will be removed from the album and will no longer have access to it.
        </Text>
        
        <View marginTop="1rem">
          <CheckboxField
            name="deletePhotos"
            value="true"
            checked={deletePhotos}
            onChange={() => setDeletePhotos(!deletePhotos)}
            label={
              <Text fontWeight={500}>
                Delete photos uploaded by these members
              </Text>
            }
          />
          
          {deletePhotos && (
            <Text fontSize="0.875rem" color="#AAAAAA" marginTop="0.5rem" marginLeft="1.75rem">
              This will permanently delete all photos these members have uploaded to this album.
              This action cannot be undone.
            </Text>
          )}
        </View>
      </ConfirmationDialog>

      {/* Invitation Modal */}
      <InviteLinkModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        tripId={tripId}
        tripName={tripName}
      />
    </View>
  );
};

export default MembersTab;